/**
 * API service for the Flow Planning Dashboard
 */
import {
    GetFlowMonitoringDataInput,
    GetFlowMonitoringDataOutput,
    CompareFlowMonitoringDataInput,
    CompareFlowMonitoringDataOutput,
    SaveEosWashReportInput,
    SaveEosWashReportOutput,
} from './model';
import { API_GATEWAY_PROCESS_NAME } from '../../../utils/constants';
import { apiRequest } from '../../../services/apiMiddleware';
import { store } from '../../../store';
import { selectStationCode } from '../../../store/slices/shiftPlanSlice';
import {
    logOperationalMetric,
    MetricType,
    RCC_SHIFT_PLAN_FLOW_PLANNING_DASHBOARD_TAB,
    GET_FLOW_MONITORING_DATA_API,
    COMPARE_FLOW_MONITORING_DATA_API,
    SAVE_EOS_WASH_REPORT_API,
} from '../../../utils/metric-utils';

/**
 * Fetch FCLM target data for the given warehouse and time window
 */
export const getFlowMonitoringData = async (
    request: GetFlowMonitoringDataInput
): Promise<GetFlowMonitoringDataOutput> => {
    const startTime = Date.now();
    const stationCode = selectStationCode(store.getState()) || '';
    const requestPayload = {
        resourcePath: '/getFlowMonitoringData',
        httpMethod: 'post',
        processName: API_GATEWAY_PROCESS_NAME,
        requestBody: request,
    };
    try {
        const response = await apiRequest(requestPayload);
        logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_FLOW_PLANNING_DASHBOARD_TAB, GET_FLOW_MONITORING_DATA_API, MetricType.SUCCESS);
        return response.data;
    } catch (error: any) {
        logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_FLOW_PLANNING_DASHBOARD_TAB, GET_FLOW_MONITORING_DATA_API, MetricType.FAILED, error);
        throw error;
    }
};

/**
 * Compare actual flow monitoring data against a baseline (e.g. latest shift plan)
 */
export const compareFlowMonitoringData = async (
    request: CompareFlowMonitoringDataInput
): Promise<CompareFlowMonitoringDataOutput> => {
    const startTime = Date.now();
    const stationCode = selectStationCode(store.getState()) || '';
    const requestPayload = {
        resourcePath: '/compareFlowMonitoringData',
        httpMethod: 'post',
        processName: API_GATEWAY_PROCESS_NAME,
        requestBody: request,
    };
    try {
        const response = await apiRequest(requestPayload);
        logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_FLOW_PLANNING_DASHBOARD_TAB, COMPARE_FLOW_MONITORING_DATA_API, MetricType.SUCCESS);
        return response.data;
    } catch (error: any) {
        logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_FLOW_PLANNING_DASHBOARD_TAB, COMPARE_FLOW_MONITORING_DATA_API, MetricType.FAILED, error);
        throw error;
    }
};

/**
 * Save a new version of the free-text EOS (end-of-shift) wash report for a shift plan.
 * Optimistic-locked on baseVersion: the service writes baseVersion + 1 and rejects with a
 * conflict if a newer version already exists.
 */
export const saveEosWashReport = async (
    request: SaveEosWashReportInput
): Promise<SaveEosWashReportOutput> => {
    const startTime = Date.now();
    const stationCode = selectStationCode(store.getState()) || '';
    const requestPayload = {
        resourcePath: '/saveEosWashReport',
        httpMethod: 'post',
        processName: API_GATEWAY_PROCESS_NAME,
        requestBody: request,
    };
    try {
        const response = await apiRequest(requestPayload);
        logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_FLOW_PLANNING_DASHBOARD_TAB, SAVE_EOS_WASH_REPORT_API, MetricType.SUCCESS);
        return response.data;
    } catch (error: any) {
        logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_FLOW_PLANNING_DASHBOARD_TAB, SAVE_EOS_WASH_REPORT_API, MetricType.FAILED, error);
        throw error;
    }
};
